import { create } from 'zustand'
import type {
  ContactDetailResponse,
  ContactListItem,
  ContactListResponse,
  ContactSortOrder,
  ListContactsParams,
} from '../api/contacts'
import { listContacts } from '../api/contacts'

export type BlockFilter = 'any' | 'blocked' | 'unblocked'
export type IdentityPresenceFilter = 'any' | 'with' | 'without'

export interface ContactsListFilters {
  q: string
  platform: string[]
  blocked: BlockFilter
  hasIdentities: IdentityPresenceFilter
  sort: ContactSortOrder
}

export interface ContactsListState {
  filters: ContactsListFilters
  items: ContactListItem[]
  cursor: string | null
  loading: boolean
  error: string | null
  initialized: boolean
  lastEventId: number
  applyFilters: (
    stationId: number,
    updater: (previous: ContactsListFilters) => ContactsListFilters,
  ) => Promise<void>
  refresh: (stationId: number) => Promise<void>
  loadMore: (stationId: number) => Promise<void>
  upsertContact: (contact: ContactListItem) => void
  upsertFromDetail: (detail: ContactDetailResponse) => void
  removeContact: (contactId: number) => void
  setBlockedState: (contactId: number, blocked: boolean) => void
  setLastContactEventId: (eventId: number) => void
  clear: () => void
}

const defaultFilters: ContactsListFilters = {
  q: '',
  platform: [],
  blocked: 'any',
  hasIdentities: 'any',
  sort: 'last_activity_desc',
}

const mapFiltersToParams = (
  stationId: number,
  filters: ContactsListFilters,
  cursor?: string | null,
): ListContactsParams => {
  const params: ListContactsParams = {
    stationId,
    q: filters.q.trim() || undefined,
    platform: filters.platform,
    sort: filters.sort,
    cursor: cursor ?? undefined,
  }

  if (filters.blocked === 'blocked') {
    params.blocked = 'true'
  } else if (filters.blocked === 'unblocked') {
    params.blocked = 'false'
  }

  if (filters.hasIdentities === 'with') {
    params.hasIdentities = 'true'
  } else if (filters.hasIdentities === 'without') {
    params.hasIdentities = 'false'
  }

  return params
}

const mergeItems = (existing: ContactListItem[], incoming: ContactListItem[]): ContactListItem[] => {
  const byId = new Map<number, ContactListItem>()
  existing.forEach((item) => byId.set(item.id, item))
  incoming.forEach((item) => byId.set(item.id, item))
  return Array.from(byId.values()).sort((a, b) => {
    const aIndex = existing.findIndex((item) => item.id === a.id)
    const bIndex = existing.findIndex((item) => item.id === b.id)
    if (aIndex === -1 && bIndex === -1) {
      return 0
    }
    if (aIndex === -1) {
      return 1
    }
    if (bIndex === -1) {
      return -1
    }
    return aIndex - bIndex
  })
}

const useContactsListStore = create<ContactsListState>((set, get) => ({
  filters: defaultFilters,
  items: [],
  cursor: null,
  loading: false,
  error: null,
  initialized: false,
  lastEventId: 0,
  applyFilters: async (stationId, updater) => {
    const nextFilters = updater(get().filters)
    set({ filters: nextFilters })
    await get().refresh(stationId)
  },
  refresh: async (stationId) => {
    if (!stationId) {
      set({ items: [], cursor: null, initialized: true })
      return
    }

    try {
      set({ loading: true, error: null })
      const response = await listContacts(mapFiltersToParams(stationId, get().filters))
      set({
        items: response.items,
        cursor: response.cursor,
        loading: false,
        initialized: true,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load contacts.'
      set({ error: message, loading: false })
    }
  },
  loadMore: async (stationId) => {
    const { cursor, filters, loading } = get()
    if (!stationId || !cursor || loading) {
      return
    }

    try {
      set({ loading: true })
      const response: ContactListResponse = await listContacts(
        mapFiltersToParams(stationId, filters, cursor),
      )
      set((state) => ({
        items: mergeItems(state.items, response.items),
        cursor: response.cursor,
        loading: false,
      }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load more contacts.'
      set({ error: message, loading: false })
    }
  },
  upsertContact: (contact) => {
    set((state) => {
      const existingIndex = state.items.findIndex((item) => item.id === contact.id)
      if (existingIndex === -1) {
        return { items: [contact, ...state.items] }
      }
      const nextItems = [...state.items]
      nextItems[existingIndex] = { ...nextItems[existingIndex], ...contact }
      return { items: nextItems }
    })
  },
  upsertFromDetail: (detail) => {
    const summary = buildListItemFromDetail(detail)
    set((state) => {
      const existingIndex = state.items.findIndex((item) => item.id === summary.id)
      if (existingIndex === -1) {
        return { items: [summary, ...state.items] }
      }
      const nextItems = [...state.items]
      nextItems[existingIndex] = summary
      return { items: nextItems }
    })
  },
  removeContact: (contactId) => {
    set((state) => ({ items: state.items.filter((item) => item.id !== contactId) }))
  },
  setBlockedState: (contactId, blocked) => {
    set((state) => ({
      items: state.items.map((item) => (item.id === contactId ? { ...item, blocked } : item)),
    }))
  },
  setLastContactEventId: (eventId) => {
    set({ lastEventId: Math.max(eventId, 0) })
  },
  clear: () => {
    set({
      filters: defaultFilters,
      items: [],
      cursor: null,
      loading: false,
      error: null,
      initialized: false,
      lastEventId: 0,
    })
  },
}))

const buildListItemFromDetail = (detail: ContactDetailResponse): ContactListItem => {
  const identityMap = new Map<string, { platform: string; count: number; blocked_count: number }>()

  detail.identities.forEach((identity) => {
    const existing = identityMap.get(identity.platform)
    if (existing) {
      existing.count += 1
      if (identity.blocked) {
        existing.blocked_count += 1
      }
    } else {
      identityMap.set(identity.platform, {
        platform: identity.platform,
        count: 1,
        blocked_count: identity.blocked ? 1 : 0,
      })
    }
  })

  return {
    id: detail.contact.id,
    name: detail.contact.name,
    location: detail.contact.location,
    notes_preview: detail.contact.notes ? detail.contact.notes.slice(0, 120) : null,
    last_activity_at: detail.contact.last_activity_at,
    last_activity_at_iso_utc: detail.contact.last_activity_at_iso_utc ?? detail.contact.last_activity_at,
    last_activity_at_epoch: detail.contact.last_activity_at_epoch ?? null,
    identities_summary: Array.from(identityMap.values()),
    blocked: detail.contact.blocked,
  }
}

export default useContactsListStore
